import { LBButton } from '@linearb/buzz';
import { CopyTextWithLabel } from '../../CopiableText';
import { BulletStep } from './jiraFormComponents';
import { SPACES } from 'common/constants/sizes';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import styled from 'styled-components';

const DEFAULT_DATA = {
  appName: 'LinearB Team Analytics',
  serviceProvider: 'LinearB Inc.',
  appUrl: 'https://app.linearb.io',
  authUrl: 'https://linearb.io',
  consumerName: 'LinearB Inc.'
};

const APPLICATION_LINK_CONFIGURATION_POSTFIX =
  '/plugins/servlet/applinks/listApplicationLinks';

const StyledJiraAction = styled(Text)`
  .MuiButton-root {
    padding: 0.4rem 0.6rem !important;
    margin: 0 0.5rem;
    min-width: auto;
  }
`;

const JiraSetupTitle = () => {
  return (
    <>
      <Text size='large' color='abbey' weight='500' pb={SPACES.medium}>
        STEP 2
      </Text>
      <Text size='large' color='gray' pb='2rem'>
        Please follow the step-by-step instructions below to connect your
        LinearB account to Jira.
      </Text>
    </>
  );
};

const IntegrateWithJiraSteps = ({
  consumerKey,
  sharedSecret,
  publicKey,
  data,
  separator,
  openApplicationLinkConfiguration,
  reauthorize
}) => {
  return (
    <>
      <BulletStep
        description={
          <StyledJiraAction size='medium' color='shuttleGray'>
            Click
            <span className='mx-1'>
              <LBButton
                variant='outline'
                size='sm'
                onClick={openApplicationLinkConfiguration}
              >
                {reauthorize ? 'Reauthorize' : 'Connect'}
              </LBButton>
            </span>
            and follow these steps:
          </StyledJiraAction>
        }
      />
      <BulletStep
        number={1}
        description={
          <Text size='large'>
            Enter the following URL and hit "Create New Link"
          </Text>
        }
        content={[
          /* eslint-disable react/jsx-key */
          <CopyTextWithLabel text={data.appUrl} width='32rem' />,
          <Text>
            Click “Continue” on the warning modal (no response for this URL)
          </Text>
          /* eslint-enable react/jsx-key */
        ]}
      />
      {separator}
      <BulletStep
        number={2}
        description={
          <Text size='large'>
            On the screen that appears, use the following:
          </Text>
        }
        content={[
          /* eslint-disable react/jsx-key */
          <CopyTextWithLabel label='Application Name' text={data.appName} />,
          <CopyTextWithLabel
            label='Service Provider'
            text={data.serviceProvider}
          />,
          <CopyTextWithLabel label='Consumer Key' text={consumerKey} />,
          <CopyTextWithLabel label='Shared Secret' text={sharedSecret} />,
          <CopyTextWithLabel label='Request Token URL' text={data.authUrl} />,
          <CopyTextWithLabel label='Access token URL' text={data.authUrl} />,
          <CopyTextWithLabel label='Authorize URL' text={data.authUrl} />,
          <Text>
            Check "Create Incoming Link" checkbox and click "Continue".
          </Text>
          /* eslint-enable react/jsx-key */
        ]}
      />
      {separator}
      <BulletStep
        number={3}
        description={
          <Text size='large'>On the next screen, use the following:</Text>
        }
        content={[
          /* eslint-disable react/jsx-key */
          <CopyTextWithLabel label='Consumer Key' text={consumerKey} />,
          <CopyTextWithLabel label='Consumer Name' text={data.consumerName} />,
          <CopyTextWithLabel
            label='Public Key'
            text={publicKey}
            preview='-----BEGIN PUBLIC KEY-----'
          />
          /* eslint-enable react/jsx-key */
        ]}
      />
    </>
  );
};

const JiraConnectStep2 = props => {
  const {
    jiraBaseURL,
    withTitle = true,
    onContinue,
    disabled,
    consumerKey,
    sharedSecret,
    publicKey,
    data = DEFAULT_DATA,
    reauthorize = false,
    separator = null
  } = props;

  const openApplicationLinkConfiguration = () => {
    const url = jiraBaseURL + APPLICATION_LINK_CONFIGURATION_POSTFIX;
    window.open(url);
  };

  return (
    <Flex
      justify='space-between'
      content='flex-start'
      items='flex-start'
      direction='column'
    >
      {withTitle && <JiraSetupTitle />}
      <IntegrateWithJiraSteps
        consumerKey={consumerKey}
        sharedSecret={sharedSecret}
        publicKey={publicKey}
        data={data}
        separator={separator}
        openApplicationLinkConfiguration={openApplicationLinkConfiguration}
        reauthorize={reauthorize}
      />
      <Flex {...(reauthorize && { justify: 'flex-end' })} width='100%'>
        <LBButton disabled={disabled} onClick={onContinue} size='lg'>
          Continue
        </LBButton>
      </Flex>
    </Flex>
  );
};
export default JiraConnectStep2;
